import React, { useState, useCallback, useEffect } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { FolderOpen, FileSpreadsheet, Headphones, FileText, Video, File, Loader2 } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { FileInfo } from '../../../services/lambdaService';
import { FlowFileSelector } from '../FlowFileSelector';
import { useUpstreamFile, useHasUpstreamEdge } from '../useUpstreamFile';

const getFileIcon = (fileName: string, contentType?: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  if (['mp3', 'm4a', 'wav', 'ogg', 'flac', 'wma', 'aac'].includes(ext) ||
      contentType?.startsWith('audio/')) return Headphones;
  if (['mp4', 'mkv', 'avi', 'mov', 'webm', 'wmv'].includes(ext) ||
      contentType?.startsWith('video/')) return Video;
  if (ext === 'pdf' || contentType === 'application/pdf') return FileText;
  if (['csv', 'xlsx', 'xls', 'tsv', 'parquet', 'json'].includes(ext) ||
      contentType?.includes('spreadsheet') || contentType?.includes('csv')) return FileSpreadsheet;
  return File;
};

export const FilePickerNode: React.FC<{ id: string; data: { label: string; color: string; selectedFile?: FileInfo } }> = ({ id, data }) => {
  const [localFile, setLocalFile] = useState<FileInfo | null>(null);
  const { updateNodeData } = useReactFlow();
  const hasUpstream = useHasUpstreamEdge(id);
  const upstreamFile = useUpstreamFile(id);

  // Auto-select upstream file when it arrives
  useEffect(() => {
    if (upstreamFile && hasUpstream) {
      updateNodeData(id, { selectedFile: upstreamFile });
    }
  }, [upstreamFile, hasUpstream, id, updateNodeData]);

  // Display priority: demo-injected > upstream (if connected) > user-picked
  const isDemoInjected = !!(data as any).selectedFile && !hasUpstream;
  const selectedFile = hasUpstream
    ? (upstreamFile || (data as any).selectedFile)
    : ((data as any).selectedFile || localFile);

  const handleFileSelect = useCallback((file: FileInfo | null) => {
    setLocalFile(file);
    updateNodeData(id, { selectedFile: file });
  }, [id, updateNodeData]);

  return (
    <div className="bg-card border rounded-xl shadow-md w-[300px] overflow-hidden">
      <div
        className="px-3 py-2 text-xs font-semibold text-white flex items-center gap-1.5"
        style={{ background: data.color }}
      >
        <FolderOpen className="h-3.5 w-3.5" />
        {data.label}
      </div>

      <div className="p-3 space-y-2">
        {/* No upstream connection: show file picker */}
        {!hasUpstream && !isDemoInjected && (
          <FlowFileSelector selectedFile={selectedFile} onFileSelect={handleFileSelect} />
        )}

        {/* Has upstream but no file yet: waiting state */}
        {hasUpstream && !selectedFile && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
            <span>Waiting for input...</span>
          </div>
        )}

        {/* Show selected file info */}
        {selectedFile && (() => {
          const Icon = getFileIcon(selectedFile.fileName, selectedFile.contentType);
          const fileType = selectedFile.fileType || selectedFile.fileName.split('.').pop()?.toUpperCase();
          return (
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-md px-2 py-1.5">
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate flex-1">{selectedFile.fileName}</span>
              {fileType && (
                <Badge variant="secondary" className="text-[10px] px-1 py-0">
                  {fileType}
                </Badge>
              )}
            </div>
          );
        })()}
      </div>

      <Handle type="target" position={Position.Left} className="!bg-muted-foreground" />
      <Handle type="source" position={Position.Right} className="!bg-muted-foreground" />
    </div>
  );
};
